import Divider from '@mui/material/Divider';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import type { Account } from '@openx/types/account.ts';
import { useSnackbar } from 'notistack';
import React, { useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useAudienceShareRecipients } from '../../hooks/useAudienceShareRecipients.ts';
import type { AudienceShares } from '../../types.ts';
import { EnabledFeeSwitch } from './EnableFeeSwitch.tsx';
import { FeeSummary } from './FeeSummary.tsx';
import { RevenueMethodRadio } from './RevenueMethodRadio.tsx';
import { SelectedPublishers } from './SelectedPublishers.tsx';

type AllowedOrganizationsContentProps = {
  audienceName: string;
};

export const AllowedOrganizationsContent = ({ audienceName }: AllowedOrganizationsContentProps) => {
  const { control } = useFormContext<AudienceShares>();
  const { enqueueSnackbar } = useSnackbar();
  const { fields, remove, append } = useFieldArray({
    name: 'audience_share',
    control,
  });

  const { error, loading: recipientsLoading, recipients: objects } = useAudienceShareRecipients();

  const observableAudienceShareValues = useWatch({
    control,
    name: 'audience_share',
  });

  const recipientMap = useMemo(
    () =>
      objects.reduce(
        (acc, recipient) => {
          acc[recipient.id] = recipient;

          return acc;
        },
        {} as Record<string, Account>
      ),
    [objects]
  );

  const optionsPerIndex = useMemo(() => {
    const selectedRecipientIds = new Set<string>();

    observableAudienceShareValues?.forEach(share => {
      if (share?.recipient_account_id) {
        share.recipient_account_id.forEach(id => {
          selectedRecipientIds.add(id);
        });
      }
    });

    const optionsPerIndex = observableAudienceShareValues.map(row => {
      const current = new Set(row?.recipient_account_id ?? []);

      return objects.reduce<string[]>((accIds, acc) => {
        if (!selectedRecipientIds.has(acc.id) || current.has(acc.id)) {
          accIds.push(acc.id);
        }
        return accIds;
      }, []);
    });

    return optionsPerIndex;
  }, [observableAudienceShareValues, objects]);

  if (error) {
    enqueueSnackbar('Could not get partner data from the server', { variant: 'error' });
    return null;
  }

  if (recipientsLoading) {
    return <Loader />;
  }

  return (
    <>
      {fields.map((field, index) => (
        <React.Fragment key={field.id}>
          <SelectedPublishers
            field={field}
            itemIndex={index}
            recipientMap={recipientMap}
            options={optionsPerIndex[index]}
            handleDelete={remove}
            append={append}
            totalItems={fields.length}
          />

          {!!field.recipient_account_id.length && <EnabledFeeSwitch field={field} itemIndex={index} />}

          {field.isFeeEnabled && (
            <>
              <RevenueMethodRadio itemIndex={index} field={field} />
              <FeeSummary field={field} itemIndex={index} audienceName={audienceName} />
            </>
          )}

          {index < fields.length - 1 && <Divider sx={{ my: 3 }} />}
        </React.Fragment>
      ))}
    </>
  );
};
